import {
	Box, Breakpoint, Button,
	ListItemButton,
	SxProps, Typography
} from "@mui/material";
import Modal from "components/controls/Modal";
import React, { FunctionComponent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import deviceSo from "stores/layout/device";
import FindField from "../fields/FindField";
import MultiSelectorBase from "./MultiSelectorBase";



interface Props {
	/** titolo sulla DIALOG */
	title: string
	/** icona significativa */
	icon: React.ReactNode
	/** indica se  la DIALOG è visibile o no */
	isOpen: boolean
	/** indica se eliminare o no l'uso del tasto "space" (usato per i tag) */
	noSpace: boolean
	/** array di ID degli item selezionati */
	itemsSelect: any[]
	/** oggetti da visualizzare */
	items: any[]
	/** testo di ricerca */
	textValue: string
	textPlaceholder?: string

	/** string "xs":0px "sm":600px "md":960px "lg":1280px "xl":1920px */
	maxWidth?: Breakpoint | false
	width?: number | string
	fullWidth?: boolean
	height?: number | string

	/** quando la DIALOG si chiude.  */
	onClose: (isOk: boolean) => void
	onChangeItemsSelect: (items: any[]) => void
	/** chiamato se il FIND cambia. Se null allora il FIND non c'e' */
	onChangeTextValue?: (text: string) => void
	onNewItem: (text: string) => boolean

	/** per un "item" restituisce il suo "text" */
	fnTextFromItem: (item: any) => React.ReactNode,
	/** per un "item" restituisce il suo "sottotitolo" */
	fnSecondaryFromItem: (item: any) => React.ReactNode,
	/** a fronte di un item restituisce il suo ID */
	fnIdFromItem: (item: any) => any,

	renderFilter?: React.ReactNode,
	renderFilterColumn?: React.ReactNode,
}

/**
 * Qui c'e' un errore concettuale. cioe' gli oggetti "selected" sono sempre ITEMS
 * invece dovrebbero essere gli ID degli items.
 * è corretto in MultiSelectorDialogBase2
 */
const MultiSelectorDialogBase: FunctionComponent<Partial<Props>> = ({
	title,
	icon,
	isOpen,
	noSpace = false,

	itemsSelect,
	items,
	textValue,
	maxWidth,
	width,
	fullWidth,
	height = "600px",
	textPlaceholder,

	onClose,
	onChangeItemsSelect,
	onChangeTextValue,
	onNewItem,

	fnTextFromItem = (item) => item,
	fnSecondaryFromItem,
	fnIdFromItem = (item) => item,

	renderFilter,
	renderFilterColumn,
}) => {

	// HOOKs
	const { t } = useTranslation()
	const [oldSelect, setOldSelect] = useState<any[]>()
	useEffect(() => {
		if (!isOpen) {
			setOldSelect(null)
			return
		}
		onChangeTextValue?.("")
		setOldSelect(itemsSelect)
	}, [isOpen])

	// HANDLER
	const handleChangeTextValue = (e: any) => {
		onChangeTextValue?.(e.target.value)
	}
	const handleClickOk = () => {
		onClose(true)
	}
	const handleClickCancel = () => {
		onChangeItemsSelect(oldSelect)
		onClose(false)
	}
	const handleCreateNew = (e) => {
		e.preventDefault()
		addNewItem(textValue)
	}
	const handleKeyDown = (e) => {
		switch (e.code) {
			case "Enter":
				addNewItem(textValue)
				break
			case "Space":
				if (!noSpace) return
				e.preventDefault()
				break
			default:
		}
	}

	// RENDER 
	/** restituisce se due item sono uguali */
	const isEqual = (item1: any, item2: any) => fnIdFromItem(item1) == fnIdFromItem(item2)
	/** restituisce se un ITEM è selezionato (o no) */
	const isSelect = (item: any) => itemsSelect?.some(i => isEqual(i, item)) ?? false
	/** cerca un solo ITEM tramite una "text" */
	const findIndexFromText = (text = "") => {
		text = text.trim().toLowerCase()
		if (text.length == 0) return -1
		return items.findIndex(i => fnTextFromItem(i) == text)
	}
	/** indica che si è inserito una text nuova e che quindi potrei suggerire di creare un nuovo elemento */
	const isNew = () => onNewItem && textValue.length > 0 && findIndexFromText(textValue) == -1

	const selectItem = (item: any) => {
		if (isSelect(item)) return
		const newItemsSelect = [...(itemsSelect ?? []), item]
		onChangeItemsSelect(newItemsSelect)
	}
	const addNewItem = (text) => {
		if (!isNew()) return
		onChangeTextValue?.("")
		if (!onNewItem(text)) return
		selectItem(text)
	}

	return (
		<Modal
			sx={{ ".MuiDialog-paper": { width: width } }}
			icon={icon}
			maxWidth={maxWidth}
			fullWidth={fullWidth}
			title={title}
			isOpen={isOpen}
			onClose={handleClickCancel}
			actionsRender={<>
				<Box sx={{ flex: "1 1 auto" }} />

				<Button data-test="dialog-btt-cancel"
					onClick={handleClickCancel}
				>
					{t("dialog.default.cancel")}
				</Button>

				<Button data-test="dialog-btt-ok"
					onClick={handleClickOk} color="secondary"
				>
					{t("dialog.default.ok")}
				</Button>

			</>}
		>
			<Box sx={cssBody(height)}>

				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>

					{/* TEXT FILTER */}
					{onChangeTextValue && (
						<FindField sx={{ flex: 1 }}
							autoFocus={deviceSo.isDesktop()}
							placeholder={textPlaceholder ?? t("dialog.tag.label")}
							value={textValue}
							onChange={handleChangeTextValue}
							onKeyDown={handleKeyDown}
						/>
					)}

					{renderFilter}

				</Box>

				{renderFilterColumn}


				{/* ADD NEW ITEM */}
				{isNew() && <ListItemButton
					onMouseDown={handleCreateNew}
				>
					<Typography>{t("dialog.tag.add-tag")} <Box sx={{ color: "secondary.main" }}>
						{textValue}
					</Box></Typography>
				</ListItemButton>}


				{/* LIST */}
				<MultiSelectorBase
					itemsSelect={itemsSelect}
					items={items}
					onChangeItemsSelect={onChangeItemsSelect}
					fnTextFromItem={fnTextFromItem}
					fnSecondaryFromItem={fnSecondaryFromItem}
					fnIdFromItem={fnIdFromItem}
				/>
			</Box>
		</Modal>
	)
}

export default MultiSelectorDialogBase

const cssBody = (height: number | string): SxProps => ({
	display: "flex",
	flexDirection: "column",
	height: height ?? "unset",
	gap: "5px",
})
